/**
 * calb008/calb032 progressive session mutation classifier.
 *
 * Calibration company semantics only: the status column is `validity`
 * (Aktívny / Vyradený). OTK partner-status fields are deliberately absent.
 */
export const CALIBRATION_COMPANIES_UPDATED_EVENT = 'calibration_companies:updated';

export const SESSION_SAFE_UPDATE = 'SESSION_SAFE_UPDATE';
export const SESSION_INVALIDATING = 'SESSION_INVALIDATING';

export const IMPLICIT_UPDATE_AUDIT_FIELDS = Object.freeze([
  'updated_at',
  'updated_by_user',
  'updated_by_user_id',
]);

const ALWAYS_INVALIDATING_FIELDS = new Set([
  'types',
  'validity',
  'company_status_display',
  'certificates',
  'deactivate_certificates',
  'activate_certificates',
  'id',
]);

const SCALAR_FIELDS = [
  'name',
  'defined_id',
  'address',
  'tel_number',
  'fax',
  'email',
  'web_address',
  'contact_person',
  'description',
  'country',
  'types',
  'validity',
  'company_status_display',
  'certificates',
  'deactivate_certificates',
  'activate_certificates',
];

const FIELD_ALIASES = {
  name: ['name'],
  defined_id: ['defined_id'],
  address: ['address'],
  tel_number: ['tel_number'],
  fax: ['fax'],
  email: ['email'],
  web_address: ['web_address'],
  contact_person: ['contact_person'],
  description: ['description'],
  country: ['country'],
  types: ['types'],
  validity: ['validity', 'company_status_display'],
  company_status_display: ['company_status_display', 'validity'],
  certificates: ['certificates'],
  deactivate_certificates: ['deactivate_certificates', 'certificates'],
  activate_certificates: ['activate_certificates', 'certificates'],
  id: ['id'],
  updated_at: ['updated_at'],
  updated_by_user: ['updated_at'],
  updated_by_user_id: ['updated_at'],
  created_at: ['created_at'],
  created_by_user: ['created_at'],
  created_by_user_id: ['created_at'],
};

function unwrapCompany(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && payload.data.id != null) {
    return payload.data;
  }
  return payload;
}

function scalar(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (!Array.isArray(value) && value.id != null && Object.keys(value).length <= 2) return String(value.id);
    try {
      return JSON.stringify(value);
    } catch (_e) {
      return '';
    }
  }
  return String(value);
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    const aliases = FIELD_ALIASES[field] || [field];
    aliases.forEach((alias) => expanded.add(alias));
  });
  return expanded;
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function isDefaultNameAscSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'name' && dir === 'asc';
}

export function isTableCompleteCalibrationCompanyRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  return row.id != null && row.id !== '';
}

export function collectCalibrationCompanyChangedFields(previous, next, options = {}) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const nxt = next && typeof next === 'object' ? next : {};
  const changed = [];
  SCALAR_FIELDS.forEach((key) => {
    if (scalar(prev[key]) !== scalar(nxt[key])) changed.push(key);
  });
  if (prev.id != null && nxt.id != null && String(prev.id) !== String(nxt.id)) changed.push('id');
  if (options.implicitAudit) changed.push(...IMPLICIT_UPDATE_AUDIT_FIELDS);
  return Array.from(new Set(changed));
}

export function classifyCalibrationCompanyListMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  if (detail.operation === 'noop') return null;
  if (detail.operation !== 'update') return SESSION_INVALIDATING;
  if (!Array.isArray(detail.changedFields) || detail.changedFields.length === 0) return SESSION_INVALIDATING;
  const row = unwrapCompany(detail.row);
  if (!isTableCompleteCalibrationCompanyRow(row)) return SESSION_INVALIDATING;
  const id = detail.id ?? row.id;
  if (id == null || id === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(id)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;

  const changedFields = Array.from(new Set([
    ...detail.changedFields,
    ...IMPLICIT_UPDATE_AUDIT_FIELDS,
  ]));
  const expanded = expandFields(changedFields);
  for (const field of expanded) {
    if (ALWAYS_INVALIDATING_FIELDS.has(field)) return SESSION_INVALIDATING;
  }
  if (changedFields.some((field) => ALWAYS_INVALIDATING_FIELDS.has(field))) return SESSION_INVALIDATING;

  const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
  for (const filter of filters) {
    if (!filter?.field || !hasActiveValue(filter.value)) continue;
    const aliases = expandFields([filter.field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
  for (const sorter of sorters) {
    const field = sorter?.field || sorter?.column;
    if (!field) continue;
    if (isDefaultNameAscSorter(sorter) && !expanded.has('name')) continue;
    const aliases = expandFields([field]);
    for (const alias of aliases) {
      if (expanded.has(alias)) return SESSION_INVALIDATING;
    }
  }

  return SESSION_SAFE_UPDATE;
}

export function applyPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      if (!row || row.id == null) return row;
      return overlay.get(String(row.id)) || row;
    }),
  };
}

export function normalizeCalibrationCompanyTimestamp(value) {
  if (value == null || value === '') return '';
  const parsed = Date.parse(String(value).replace(' ', 'T'));
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

export function isAcknowledgedCalibrationCompanyUpdateEcho(payload, overlay) {
  if (!payload || typeof payload !== 'object') return false;
  if (!overlay || typeof overlay.get !== 'function') return false;
  const id = payload.id;
  const updatedAt = payload.updated_at;
  if (id == null || id === '' || updatedAt == null || updatedAt === '') return false;
  const row = overlay.get(String(id));
  if (!row || typeof row !== 'object' || row.updated_at == null || row.updated_at === '') return false;
  return normalizeCalibrationCompanyTimestamp(row.updated_at) === normalizeCalibrationCompanyTimestamp(updatedAt);
}

export function emitCalibrationCompanyListMutation(detail) {
  window.dispatchEvent(new CustomEvent(CALIBRATION_COMPANIES_UPDATED_EVENT, {
    detail: detail && typeof detail === 'object' ? detail : { operation: 'invalidate' },
  }));
}

export function unwrapCalibrationCompanyPayload(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  return unwrapCompany(payload.data != null && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? (payload.data.data != null && typeof payload.data.data === 'object' && !Array.isArray(payload.data.data) && payload.data.data.id != null
      ? payload.data.data
      : payload.data)
    : payload);
}
